import axios, { AxiosError, AxiosResponse } from 'axios';
import { API_BASE_URL } from './constants';
import { redirectToLogin, refreshAccessToken } from './auth';
import { getAccessToken, getRefreshToken, saveAuthTokens } from './tokenStorage';
import { GENERIC_ERROR_MESSAGE, SUBSCRIPTION_ERROR_MESSAGE } from 'src/constants/messages';
import {
  getErrorPayload,
  logSafeAxiosError,
  markReportedToUser,
  shouldShowGenericError,
  shouldSuppressToast,
  shouldTreatAsSuccess,
  showErrorToastOnce,
} from './errorHandling';
import { CustomAxiosRequestConfig, RetryQueueItem } from './types';

const refreshAndRetryQueue: RetryQueueItem[] = [];
let isRefreshing = false;
/** Set when a request that already carried a fresh token still came back 401. */
let sessionExpired = false;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Each caller gets its own 401 payload, not the refresh endpoint's.
const rejectQueuedRequests = () => {
  refreshAndRetryQueue.splice(0).forEach(({ error, reject }) => {
    reject(markReportedToUser(getErrorPayload(error)));
  });
};

const retryQueuedRequests = () => {
  refreshAndRetryQueue.splice(0).forEach(({ config, resolve, reject }) => {
    apiClient.request(config).then(resolve).catch(reject);
  });
};

const rejectWithPayload = (error: AxiosError, reported = false) => {
  const payload = getErrorPayload(error);
  return Promise.reject(reported ? markReportedToUser(payload) : payload);
};

apiClient.interceptors.request.use(
  config => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `JWT ${token}`;
    }

    config.headers['X-User-Agent'] = navigator.userAgent;
    config.validateStatus = status =>
      shouldTreatAsSuccess(config as CustomAxiosRequestConfig, status);

    return config;
  },
  error => {
    if (!axios.isAxiosError(error) || !shouldSuppressToast(error)) {
      showErrorToastOnce(GENERIC_ERROR_MESSAGE);
    }

    logSafeAxiosError(error);
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    if (error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    const originalRequest = error.config as CustomAxiosRequestConfig | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        originalRequest._retry = true;
        return new Promise<AxiosResponse>((resolve, reject) => {
          refreshAndRetryQueue.push({ config: originalRequest, error, resolve, reject });
        });
      }

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        logSafeAxiosError(error);
        const hadSession = !!getAccessToken();
        if (hadSession) {
          redirectToLogin();
        }
        return rejectWithPayload(error, hadSession);
      }

      isRefreshing = true;
      sessionExpired = false;
      originalRequest._retry = true;

      try {
        const tokens = await refreshAccessToken(refreshToken);
        saveAuthTokens(tokens);
      } catch (refreshError) {
        isRefreshing = false;
        logSafeAxiosError(refreshError);
        rejectQueuedRequests();
        redirectToLogin();
        return rejectWithPayload(error, true);
      }

      // Gate is held until the retry settles, so a 401 arriving in that window queues instead
      // of starting a second refresh. Anything queued in that window is drained on the way out.
      try {
        retryQueuedRequests();
        return await apiClient(originalRequest);
      } finally {
        isRefreshing = false;

        // A retry failing on its own merits (500, network) says nothing about the token, so the
        // queue still gets it. Only a second 401 means the session is gone.
        if (sessionExpired) {
          rejectQueuedRequests();
        } else {
          retryQueuedRequests();
        }
      }
    }

    // Fresh token still refused: sign out rather than leave the user signed in but unauthorized.
    if (error.response?.status === 401 && originalRequest?._retry) {
      sessionExpired = true;
      logSafeAxiosError(error);
      redirectToLogin();
      return rejectWithPayload(error, true);
    }

    let reported = false;
    if (!shouldSuppressToast(error)) {
      if (error.response?.status === 402) {
        showErrorToastOnce(SUBSCRIPTION_ERROR_MESSAGE);
        reported = true;
      } else if (shouldShowGenericError(error)) {
        showErrorToastOnce(GENERIC_ERROR_MESSAGE);
        reported = true;
      }
    }

    logSafeAxiosError(error);
    return rejectWithPayload(error, reported);
  },
);

export default apiClient;
